import { useEffect, useRef, useState } from 'react';
import { env } from '@noor/config';
import { readAuthToken } from '@noor/api';
import {
  readSocketFrame,
  authFrame,
  pingFrame,
  type LegacyChatSocketAction,
} from '../lib/protocol';

/**
 * The legacy WebSocket connection.
 *
 * Reproduces the old panel's socket lifecycle (`merchant-panel/src/hooks/
 * useWebSocket.ts`): connect, answer `waiting_auth` with the access token, ping
 * every 30s once authenticated, and reconnect when the server drops us. The
 * protocol itself lives in `lib/protocol` so it can be tested without a socket;
 * this hook owns only the connection and its timers.
 *
 * Two deliberate divergences from the sources, both fixing real defects:
 *
 * 1. **No module-level socket.** The old panel holds `let ws` at module scope,
 *    so a second mount silently reuses (or orphans) the first one's connection
 *    and its `onmessage` closure — which is why its `reconnect()` exists at all.
 *    The socket lives in a ref here, one per hook instance, closed on unmount.
 *
 * 2. **Reconnection does not give up.** cpanel stops after 3 attempts and shows
 *    a toast; a merchant who leaves the orders screen open over lunch comes back
 *    to a dead socket that never recovers, with a stale list that looks live.
 *    This backs off (1s → 30s, capped) and keeps trying, because a silently
 *    stale orders list is worse than a reconnect attempt.
 *
 * `auth_failed` is the one terminal case: retrying with a token the server has
 * already rejected just loops, so it stops and waits for a new token.
 */

/** Keepalive period. The old panel uses 30s; the server answers a bare `pong`. */
const PING_INTERVAL_MS = 30_000;

/** Reconnect backoff bounds. */
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

interface UseLegacySocketOptions {
  /** Called on a `refresh` push — the orders list should refetch. */
  onRefresh?: () => void;
  /** Called on a chat push — message, new thread, or status change. */
  onChatEvent?: (action: LegacyChatSocketAction) => void;
  /** Connect only while true (e.g. only in legacy mode, only when signed in). */
  enabled: boolean;
}

export function useLegacySocket({
  onRefresh,
  onChatEvent,
  enabled,
}: UseLegacySocketOptions): {
  isConnected: boolean;
} {
  const [isConnected, setIsConnected] = useState(false);

  // The callbacks are read through refs so a caller passing inline functions
  // doesn't tear down and rebuild the socket on every render.
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);
  const onChatEventRef = useRef(onChatEvent);
  useEffect(() => {
    onChatEventRef.current = onChatEvent;
  }, [onChatEvent]);

  useEffect(() => {
    if (!enabled) return;

    let socket: WebSocket | null = null;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    /** Set on unmount so in-flight handlers don't resurrect a closed socket. */
    let disposed = false;

    const clearPing = () => {
      if (pingTimer) clearInterval(pingTimer);
      pingTimer = undefined;
    };

    const connect = () => {
      if (disposed) return;

      // Read the token at CONNECT time, not once outside: a reconnect after a
      // token refresh must present the new one.
      const token = readAuthToken();
      if (!token) return;

      socket = new WebSocket(env.VITE_LEGACY_SOCKET_URL);

      socket.onopen = () => {
        if (disposed) return;
        setIsConnected(true);
      };

      socket.onmessage = (event: MessageEvent<string>) => {
        if (disposed) return;
        const action = readSocketFrame(event.data);

        switch (action.kind) {
          case 'authenticate': {
            // Re-read: the handshake can outlive a token refresh.
            const current = readAuthToken();
            if (current) socket?.send(authFrame(current));
            break;
          }
          case 'authenticated': {
            // Only start pinging once the server has accepted us — an
            // unauthenticated PING is what triggers `auth_failed` on this
            // backend.
            attempt = 0;
            clearPing();
            pingTimer = setInterval(() => {
              if (socket?.readyState === WebSocket.OPEN) socket.send(pingFrame());
            }, PING_INTERVAL_MS);
            break;
          }
          case 'auth-failed': {
            // Terminal: don't reconnect into the same rejection.
            disposed = true;
            socket?.close();
            break;
          }
          case 'refresh': {
            onRefreshRef.current?.();
            break;
          }
          case 'chat-message':
          case 'new-chat':
          case 'new-chat-status': {
            onChatEventRef.current?.(action);
            break;
          }
          case 'ignore':
            break;
        }
      };

      socket.onclose = () => {
        clearPing();
        setIsConnected(false);
        socket = null;
        if (disposed) return;

        // Exponential backoff, capped. Keeps trying indefinitely — see above.
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };

      // `onerror` is always followed by `onclose`, which owns the retry, so
      // this only records the failure — retrying here too would schedule two
      // reconnects for one error.
      socket.onerror = () => {
        setIsConnected(false);
      };
    };

    connect();

    return () => {
      disposed = true;
      clearPing();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) {
        // Drop the handler before closing, or teardown schedules a reconnect.
        socket.onclose = null;
        socket.close();
      }
      setIsConnected(false);
    };
  }, [enabled]);

  return { isConnected };
}
